import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Subscription, tap } from 'rxjs';
import { ActivityService } from 'src/app/service/activity.service';
import { AgentService } from 'src/app/service/agent.service';
import { AuthenticationService } from 'src/app/service/authentication.service';
import { DataService } from 'src/app/service/data.service';
import { EventService } from 'src/app/service/event.service';
import { Events, MsgPayLoad, WebsocketService } from 'src/app/service/websocket.service';
import { ActionMenuEvent, ActionMenuIcon, ActionMenuItem } from '../controls/actionmenu/action-menu.component';
import { ColumnSort, ColumnType, DataTableColumn, DataTableMultiColumnFilter, DataTableRow } from '../controls/datatable/datatable.component';
import { Modal } from '../controls/modal/modal.service';
import { SystemsComponent } from '../systems/systems.component';
import { PageCount, PageEvent } from '../controls/pagination/pagination.component';
import { Agent } from 'src/app/model/Agent';
import { Utils } from 'src/app/utils';
import { CookieService } from 'ngx-cookie-service';
import { SolutionService } from 'src/app/service/solution.service';

export enum SystemsOverviewTab { 
  MANAGED, 
  PENDING 
}

@Component({
    selector: 'app-systems-overview',
    templateUrl: './systems-overview.component.html',
    styleUrls: ['./systems-overview.component.css'],
    standalone: false
})
export class SystemsOverviewComponent extends SystemsComponent implements OnInit, OnDestroy {
  tableId: string = "systems-overview-table";
  title: string = $localize`All Environments`;
  agentCols: DataTableColumn[] = [];
  multiColFilter:Object  = new Object();
  loading:boolean = true;
  total:number = 0;
  selectedSystem: Agent;
  selectedSystemId: string;
  agents: Agent[] = [];
  agentRows: DataTableRow[] = [];
  products: DataTableRow[] = [];
  environments: DataTableRow[] = [];
  pageInfo: PageEvent = new PageEvent(1, 0, PageCount.TWENTYFIVE);
  hostSortDirection: ColumnSort = ColumnSort.ASC;
  nameSortDirection: ColumnSort = ColumnSort.ASC;
  isHostDecendingOrder: boolean = false;
  isNameDecendingOrder: boolean = false;
  sortColumn: string = 'host';
  hostFilter:DataTableMultiColumnFilter = new DataTableMultiColumnFilter($localize`Search host`);
  nameFilter:DataTableMultiColumnFilter = new DataTableMultiColumnFilter($localize`Search name`);
  envFilter:DataTableMultiColumnFilter = new DataTableMultiColumnFilter($localize`Search environments`);
  prodFilter:DataTableMultiColumnFilter = new DataTableMultiColumnFilter($localize`Search products`);
  tableSettingsId: string = "systems-overview-table-settings";
  tableSettings: Object = new Object();

  solutionInfoSubscription: Subscription;
  eventMessageSubscription: Subscription;
  
  constructor(protected agentService: AgentService, protected eventService: EventService, protected router: Router,
    protected dataSrv: DataService, protected route: ActivatedRoute, protected modal: Modal,
    protected wsService: WebsocketService, protected authService: AuthenticationService,
    protected activityService: ActivityService, protected cookieSrv: CookieService,
    protected solutionService: SolutionService) {
    super(agentService, eventService, router, dataSrv, route, modal, wsService, authService, activityService, solutionService);

    this.agentCols = Array<DataTableColumn>(
      new DataTableColumn("name", $localize`Host`, ColumnType.STRING, true, "16", this.hostSortDirection, undefined, undefined, true, this.hostFilter),
      new DataTableColumn("nickName", $localize`Name`, ColumnType.STRING, false, "16", this.nameSortDirection, undefined, undefined, true, this.nameFilter),
      new DataTableColumn("osName", $localize`Operating System`, ColumnType.STRING, false, "12"),
      new DataTableColumn("statusExt", $localize`Status`, ColumnType.STATUSEXT, false, "8"),
      new DataTableColumn("envStr", $localize`Environments`, ColumnType.STRING, false, "19", undefined, undefined, undefined, false, this.envFilter),
      new DataTableColumn("enabledProdStr", $localize`Products`, ColumnType.STRING, false, "20", undefined, undefined, undefined, false, this.prodFilter),
      new DataTableColumn("connectedDateTime", $localize`Connected`, ColumnType.DATEFROMNOW, false, "9")
    );

    try {
      if(this.cookieSrv.get(this.tableSettingsId)){
        this.tableSettings = JSON.parse(this.cookieSrv.get(this.tableSettingsId)); 
        if(this.tableSettings){
          if(this.tableSettings['filter']){
            this.hostFilter.value = this.tableSettings['filter'].name ? this.tableSettings['filter'].name : '';
            this.nameFilter.value = this.tableSettings['filter'].nickName ? this.tableSettings['filter'].nickName : '';
            this.envFilter.value = this.tableSettings['filter'].envStr ? this.tableSettings['filter'].envStr : '';
            this.prodFilter.value = this.tableSettings['filter'].enabledProdStr ? this.tableSettings['filter'].enabledProdStr : '';
            this.multiColFilter = this.tableSettings['filter'];
          }
          if(this.tableSettings['page']){
            this.pageInfo = this.tableSettings['page'];
          }
          if(this.tableSettings['sort']){
            this.isHostDecendingOrder = this.tableSettings['sort'];
            this.hostSortDirection = this.isHostDecendingOrder ? ColumnSort.DESC : ColumnSort.ASC;
            this.agentCols[0].sortDirection = this.hostSortDirection;
          }
          if(this.tableSettings['nameSort']){
            this.isNameDecendingOrder = this.tableSettings['nameSort'];
            this.nameSortDirection = this.isNameDecendingOrder ? ColumnSort.DESC : ColumnSort.ASC;
            this.agentCols[1].sortDirection = this.nameSortDirection;
          }
          if(this.tableSettings['sortColumn']){
              this.sortColumn =  this.tableSettings['sortColumn'];
              this.agentCols.forEach((col:DataTableColumn) => {
                col.sorted = col.field == this.sortColumn;
              });
            }
        }
      }
    } catch (error) {
      console.error("Error getting the table settings: " + error);
    }
  }

  ngOnInit(): void {
    this.solutionInfoSubscription = this.eventService.currentSolutionInfo$.subscribe(newSolutionInfo => {
      this.solutionInfo = newSolutionInfo;
      this.loadAgentsForUser();
    });

    this.eventMessageSubscription = this.eventService.eventMessage$.subscribe(msg => {
      this.processEvent(msg);
    });
  }

  ngOnDestroy() {
    this.solutionInfoSubscription?.unsubscribe();
    this.eventMessageSubscription?.unsubscribe();
  }

  processEvent(msg: MsgPayLoad): void {
    switch (msg.eventType) {
      case Events.AgentRefresh:
        this.loadAgentsForUser(false);
        break;
      case Events.AgentManifestStart:
      case Events.AgentManifestError:
      case Events.AgentManifestComplete:
        this.loadAgentsForUser(false);
        break;
    }
  }  
  
  processAgents(): void {
    let agents: DataTableRow[] = [];

    this.agents.forEach((agent) => {
      if(agent.envStr == null){
        agent.envStr = '';
      }
      agent.name = agent.systemName;
      agent.enabledProdStr = agent.products.filter(prod => {return prod.isEnabled;}).map(prod => {
        return prod.productName + " " + prod.productVersion;
      }).join(", ");
      
      var tmpRow = new DataTableRow(agent, null);
      agent.statusExt = Utils.getAgentStatus(agent);
      let canUnregister:boolean = agent.environments.length > 0;
      //can register?
      let canRegister:boolean = this.solutionInfo.environments.length != agent.environments.length;
      if(!canRegister){
        canRegister = this.solutionInfo.environments.filter(env => agent.environments.filter(agentEnv => {
                                                                    agentEnv.id != env.id
                                                            }).length > 0).length > 0;
      }
      
      //details
      tmpRow.actions = new Array<ActionMenuItem>(
        new ActionMenuItem("Details", $localize`Details`, ActionMenuIcon.DETAILS, agent, false)
      );
      //enable/disable
      if(canUnregister){
        tmpRow.actions.push(agent.isActive ? new ActionMenuItem("Disable", $localize`Disable`, ActionMenuIcon.LOCK, agent, false) :
                                          new ActionMenuItem("Enable", $localize`Enable`, ActionMenuIcon.UNLOCK, agent, false));
        tmpRow.actions.push(new ActionMenuItem("Unregister", $localize`Unregister`, ActionMenuIcon.UNREGISTER, agent, false));
        agent.connectedDateTime = agent.approvedDateTime;
      }else{
        agent.connectedDateTime = agent.updatedDateTime;
      }
      //register/unregister
      if(canRegister){
        tmpRow.actions.push(new ActionMenuItem("Register", $localize`Register`, ActionMenuIcon.ADD, agent, false));
      }
      
      //remove
      if(!agent.isAuthorized){
        tmpRow.actions.push(new ActionMenuItem("Remove", $localize`Remove`, ActionMenuIcon.DELETE, agent, true));
      }
      agents.push(tmpRow);
    });

    this.agentRows = agents;
    this.loading = false;
  }
  
  loadAgentsForUser(showLoading:boolean = true): void {
    if(showLoading){
      this.loading = true;
    }

    this.agentService.getAgents(this.pageInfo.startingIndex, this.pageInfo.itemsPerPage, this.sortColumn, this.isDecendingOrder(), this.multiColFilter).pipe(
      tap(resp => {
        this.agents = resp.body.agents;
        this.total = resp.body.count;
        this.processAgents();
      })).subscribe();
  }
  
  actionMenuClicked(event: ActionMenuEvent): void {
    if (event.name == "Details") {
      this.router.navigate(["/systems/details/", event.data[0].agentID]);
    } else if (event.name == "Enable") {
      this.enableSystem(event.data[0].macAddress, event.data[0].systemName);
    } else if (event.name == "Disable") {
      this.disableSystem(event.data[0].macAddress, event.data[0].systemName);
    } else if (event.name == "Register") {
      this.router.navigate(["/systems/register/", event.data[0].agentID]);
    } else if (event.name == "Unregister") {
      this.unregisterSystem(event.data[0], event.data[0].environments);
    } else if (event.name == "Remove") {
      this.removeSystem(event.data[0].macAddress, event.data[0].systemName);
    }
  }

  agentRowClicked(event: any): void{
    this.router.navigate(["/systems/details/", event.data.agentID]);
  }

  onPageChange(pageEvent: PageEvent) {
    this.pageInfo = pageEvent;
    if (this.cookieSrv.get(this.tableSettingsId) != '') {
      try {
        let settings = JSON.parse(this.cookieSrv.get(this.tableSettingsId));
        settings['page'] = this.pageInfo;
        this.cookieSrv.set(this.tableSettingsId, JSON.stringify(settings));
      } catch (e) {
        //json parsing error reset to empty
        this.cookieSrv.set(this.tableSettingsId, '');
      }
    }
    this.loadAgentsForUser();
  }

  onMultiFilterChanged(cols: DataTableColumn[]): void {
    this.multiColFilter = new Object();
    this.pageInfo.pagenum = 1;
    this.pageInfo.startingIndex = 0;

    cols.forEach((col) => {
      if(col.isMultiColumnFilter()){
        this.multiColFilter[col.field] = col.multiColumnFilter.value;
      }
    });

    this.tableSettings['page'] = this.pageInfo;
    this.tableSettings['filter'] = this.multiColFilter;
    this.cookieSrv.set(this.tableSettingsId, JSON.stringify(this.tableSettings));
    this.loadAgentsForUser();
  }

  sortAgents(col: DataTableColumn) {
    if(col.header == 'Host'){
      this.isHostDecendingOrder = col.sortDirection == ColumnSort.DESC;
      this.tableSettings['sort'] = this.isHostDecendingOrder;
      this.sortColumn = 'name';
    }else if(col.header == 'Name'){
      this.isNameDecendingOrder = col.sortDirection == ColumnSort.DESC;
      this.tableSettings['nameSort'] = this.isNameDecendingOrder;
      this.sortColumn = 'nickName';
    }

    this.tableSettings['sortColumn'] = this.sortColumn;
    this.pageInfo.pagenum = 1;
    this.pageInfo.startingIndex = 0;

    this.tableSettings['page'] = this.pageInfo;
    this.cookieSrv.set(this.tableSettingsId, JSON.stringify(this.tableSettings));
    this.loadAgentsForUser();
  }

  isDecendingOrder(): boolean {
    const index:number = this.agentCols.findIndex((col:DataTableColumn) => {
      return col.sorted;
    });
    return (index > -1) ? this.agentCols[index].sortDirection == ColumnSort.DESC : false;
  }
}